import React from 'react';
import { PolicyRevision } from '../../types';
import { TimelineNode } from './TimelineNode';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { History } from 'lucide-react';

interface TimelineRailProps {
  revisions: PolicyRevision[];
  loading: boolean;
  error: string | null;
  onViewDiff: (baseId: string, targetId: string) => void;
  onReadDocument?: (revision: PolicyRevision) => void;
}

export const TimelineRail: React.FC<TimelineRailProps> = ({
  revisions,
  loading,
  error,
  onViewDiff,
  onReadDocument,
}) => {
  if (loading && revisions.length === 0) {
    return <LoadingSpinner message="Loading policy revision timeline..." />;
  }

  if (revisions.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl p-12 text-center border border-slate-200 dark:border-slate-700 space-y-3">
        <History className="w-10 h-10 text-slate-400 mx-auto" />
        <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200">
          No revision history found
        </h3>
        <p className="text-xs text-slate-500 max-w-md mx-auto">
          Trigger a manual scrape in the top right to download and establish the initial baseline snapshot for this policy.
        </p>
      </div>
    );
  }

  return (
    <div className="relative pl-3 sm:pl-6 space-y-8">
      {/* Vertical Connecting Line */}
      <div className="absolute left-[23px] sm:left-[35px] top-6 bottom-6 w-0.5 bg-slate-200 dark:bg-slate-700/80 z-0" />

      {revisions.map((rev, idx) => {
        const prevRev = idx < revisions.length - 1 ? revisions[idx + 1] : undefined;
        return (
          <TimelineNode
            key={rev.id}
            revision={rev}
            previousRevision={prevRev}
            isFirst={idx === 0}
            isLast={idx === revisions.length - 1}
            onViewDiff={onViewDiff}
            onReadDocument={onReadDocument}
          />
        );
      })}
    </div>
  );
};
